import { ApiLogger } from '../../utils';
import { useQuery } from '@tanstack/react-query';
import uuid from 'react-native-uuid';
import { get, orderBy, filter, some } from 'lodash';
import { ESignConstants } from '../../constants';
import isEqual from 'lodash.isequal';
export let ESignStatus = /*#__PURE__*/function (ESignStatus) {
  ESignStatus["COMPLETED"] = "COMPLETED";
  ESignStatus["SIGN_DISCLOSURES"] = "SIGN DISCLOSURES";
  ESignStatus["WAITING_FOR_CO_BORROWER"] = "WAITING FOR CO-BORROWER";
  ESignStatus["SCHEDULE_CLOSING"] = "SCHEDULE_CLOSING";
  return ESignStatus;
}({});
const useESignDetailsQuery = _ref => {
  let {
    url,
    logApiEvent,
    authToken,
    loanNumber,
    select,
    refetchInterval,
    setRefetchInterval,
    enabled,
    loanStatus,
    xApiKey
  } = _ref;
  const correlationId = uuid.v4().toString();
  const config = {
    method: 'get',
    url: `${url}/${loanNumber}/esign/packages`,
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'x-correlation-id': correlationId,
      'x-api-key': xApiKey
    }
  };
  return useQuery({
    //@ts-ignore
    queryKey: ['e-sign', loanNumber],
    queryFn: () => ApiLogger(logApiEvent, config),
    select,
    refetchInterval,
    enabled,
    //@ts-ignore
    structuralSharing: (oldData, newData) => {
      if (oldData && newData) {
        const oldDataArray = [];
        oldDataArray.push(oldData);
        const newDataArray = [];
        newDataArray.push(newData);
        const oldPackages = reduceLoanPackages(oldDataArray);
        const newPackages = reduceLoanPackages(newDataArray);
        const oSigned = oldPackages.map(oe => ({
          signed: oe.signed
        }));
        const nSigned = newPackages.map(ne => ({
          signed: ne.signed
        }));
        if (!isEqual(oSigned, nSigned)) {
          setRefetchInterval(false);
        }
      }
      return newData;
    },
    initialData: loanStatus >= 40 && loanStatus <= 42 ? [{
      title: 'Disclosures',
      name: 'Schedule Closing',
      details: "You'll soon receive an email from Freedom with instructions to schedule your closing",
      infoText: '',
      color: '#214781',
      isDisabled: true,
      ribbonColor: '#214781',
      status: ESignStatus.SCHEDULE_CLOSING
    }] : undefined
  });
};
const useESignTokenQuery = _ref2 => {
  let {
    url,
    logApiEvent,
    authToken,
    requestBody,
    loanNumber
  } = _ref2;
  const correlationId = uuid.v4().toString();
  const config = {
    method: 'post',
    url,
    headers: {
      'Authorization': authToken,
      'channel': 'web',
      'division': 'web',
      'application': 'web',
      'Content-Type': 'application/json',
      'x-correlation-id': correlationId
    },
    data: JSON.stringify(requestBody)
  };
  return useQuery({
    queryKey: ['fetch-esign-token', loanNumber],
    queryFn: async () => ApiLogger(logApiEvent, config),
    enabled: false,
    cacheTime: 0
  });
};
const reduceLoanPackages = packages => packages.reduce((result, current) => {
  if (current && Array.isArray(current.loanPackages) && current.loanPackages.length) {
    return result.concat(current.loanPackages.sort((a, b) => b.signingFlag - a.signingFlag));
  }
  return result;
}, []);
const transformLoanPackages = response => {
  const packages = [];
  packages.push(response);
  if (!packages || !Array.isArray(packages) || !packages.length) return undefined;
  const loanPackages = reduceLoanPackages(packages);
  if (Array.isArray(loanPackages) && loanPackages.length >= 1) {
    var updatedDisclosure = [];
    var jointLoanPackages = orderBy(loanPackages.filter(item => item.jointBorrowerPackage || item.signingFlag), ['signingFlag'], ['desc']);
    if (jointLoanPackages.length >= 1) {
      const nonJointUnSignedLoanPackages = filter(loanPackages, function (item) {
        return !item.jointBorrowerPackage && !item.signingFlag && !item.signed;
      });
      const isUnSignedJointAccountfound = some(jointLoanPackages, function (item) {
        return !item.signed;
      });
      jointLoanPackages.forEach(obj => {
        if (nonJointUnSignedLoanPackages.length >= 1) {
          var _updatedDisclosure;
          let statusMessage;
          let status = '';
          // joint +nonjoint unsigned accounts
          status = `${obj.signed}${true}`;
          // all joint account signed but non joint unsigned account exsits
          if (!isUnSignedJointAccountfound) {
            var _nonJointUnSignedLoan;
            statusMessage = nonJointUnSignedLoanPackages.length === 1 ? `Waiting for ${(_nonJointUnSignedLoan = nonJointUnSignedLoanPackages[0]) === null || _nonJointUnSignedLoan === void 0 ? void 0 : _nonJointUnSignedLoan.name}` : 'Waiting for additional borrowers to sign.';
          }
          // signed joint accounts + nonjoint signed accounts
          (_updatedDisclosure = updatedDisclosure) === null || _updatedDisclosure === void 0 || _updatedDisclosure.push(generateDisclosure(obj, status, statusMessage));
        } else if (isUnSignedJointAccountfound) {
          var _updatedDisclosure2;
          //multi unsigned joint accounts + 0 non joint accounts
          (_updatedDisclosure2 = updatedDisclosure) === null || _updatedDisclosure2 === void 0 || _updatedDisclosure2.push(generateDisclosure(obj, `${obj.signed}`));
        } else {
          // all signed joint accounts
          updatedDisclosure = undefined;
        }
      });
      return updatedDisclosure;
    }
  }
  return undefined;
};
const generateDisclosure = (obj, status, statusMessage) => {
  const result = get(ESignConstants.CURRENT_CO_BORROWER, status, {});
  return {
    ...result,
    name: 'Disclosures',
    userName: (obj === null || obj === void 0 ? void 0 : obj.name) || '',
    details: 'Documents that provide information to borrowers about the terms and conditions of their mortgage loan, including interest rates, fees, and other costs associated with the loan.',
    docUrl: obj === null || obj === void 0 ? void 0 : obj.docURL,
    status: statusMessage ? statusMessage : result === null || result === void 0 ? void 0 : result.status
  };
};
const selectDisclosures = (response, statusCode) => {
  let disclosures;
  let updatedDisclosures = [];
  if (response) {
    disclosures = transformLoanPackages(response);
  }
  if (statusCode >= 40 && statusCode <= 42) {
    updatedDisclosures === null || updatedDisclosures === void 0 || updatedDisclosures.push({
      title: 'Disclosures',
      name: 'Schedule Closing',
      details: "You'll soon receive an email from Freedom with instructions to schedule your closing",
      infoText: '',
      color: '#214781',
      isDisabled: true,
      ribbonColor: '#214781',
      status: ESignStatus.SCHEDULE_CLOSING,
      userName: ''
    });
    return updatedDisclosures;
  }
  return disclosures;
};
export { useESignDetailsQuery, useESignTokenQuery, transformLoanPackages, selectDisclosures };
//# sourceMappingURL=index.js.map